import { projectId } from '../../utils/supabase/info';
import { getAuthHeaders } from '../../features/statistics/api/auth-headers';

const BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709/openai`;

export const openaiApi = {
  async generateCaption(params: {
    existingText?: string;
    imageUrl?: string;
    accountName?: string;
    postType?: 'post' | 'reel';
  }): Promise<{ caption: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/generate-caption`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при генерации описания');
    }

    return response.json();
  },

  async generateCaptionsBulk(params: {
    existingText?: string;
    imageUrl?: string;
    accounts: Array<{ id: string; name: string }>;
    postType?: 'post' | 'reel';
  }): Promise<{ captions: Array<{ accountId: string; caption?: string; error?: string }> }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/generate-captions-bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при генерации описаний');
    }

    return response.json();
  },

  async generateTitlesBulk(params: {
    existingText?: string;
    imageUrl?: string;
    accounts: Array<{ id: string; name: string }>;
  }): Promise<{ titles: Array<{ accountId: string; title?: string; error?: string }> }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/generate-titles-bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при генерации заголовков');
    }

    return response.json();
  },

  async generateTextGoal(caption: string): Promise<{ goal: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/generate-text-goal`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ caption }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при генерации цели текста');
    }

    return response.json();
  },

  /**
   * Отбор комментариев для отчёта: возвращает id тех, что годятся в витрину.
   * `keep: null` значит «проверка не завершилась»: комментарии не включаются,
   * вызывающий показывает предупреждение о сбое проверки.
   */
  async screenComments(
    comments: Array<{ id: string; text: string }>,
  ): Promise<{ keep: string[] | null }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/screen-comments`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ comments }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Ошибка отбора комментариев');
    }

    return response.json();
  },

  async translateText(text: string): Promise<{ translation: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/translate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при переводе');
    }

    return response.json();
  },

  // Один заголовок по описанию публикации — для отчёта по аккаунтам, где текст
  // написан в подписи, а не нанесён на кадр.
  async captionHeadline(caption: string): Promise<{ headline: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/caption-headline`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ caption }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Ошибка при генерации заголовка');
    }

    return response.json();
  },

  // OCR: распознаёт текст, нанесённый поверх кадра (для отчёта по статистике).
  async extractOverlayText(imageUrl: string): Promise<{ text: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/extract-overlay-text`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageUrl }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка при распознавании текста');
    }

    return response.json();
  },
};
